import { cn, cva, type VariantProps } from 'noddi-ui-common';
import { Slot as SlotPrimitive } from 'radix-ui';

// Background doubles as the border so the pill reads as a soft fill (no outline). The
// `[data-badge-dot]` selector tints the optional `withCircle` dot with the variant's accent color.
// `successSolid` is the one filled variant — reserved for the single strongest signal on a surface
// (e.g. a live route's "In progress"); the soft `success` stays the default for ordinary states.
// It fills with `successDark` rather than `success`: white on the base hue is only 4.53:1.
// Never wider than its container, where a long label ends in an ellipsis; a sibling never squeezes it.
const badgeVariants = cva(
  'inline-flex w-fit max-w-full shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        default: 'border-lightPurple bg-lightPurple text-darkPurple [&_[data-badge-dot]]:bg-[#7F5CBA]',
        destructive: 'border-danger50 bg-danger50 text-dangerDark [&_[data-badge-dot]]:bg-danger',
        success: 'border-success20 bg-success20 text-successDark [&_[data-badge-dot]]:bg-success',
        successSolid: 'border-successDark bg-successDark text-white [&_[data-badge-dot]]:bg-white',
        warning: 'border-warning50 bg-warning50 text-warningDark [&_[data-badge-dot]]:bg-warning',
        outline: 'border-gray-300 bg-white text-gray-700 [&_[data-badge-dot]]:bg-gray-400',
        info: 'border-blue-50 bg-blue-50 text-blue-800 [&_[data-badge-dot]]:bg-blue-600'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

export type BadgeVariant = VariantProps<typeof badgeVariants>['variant'];

export const NoddiBadge = ({
  className,
  variant,
  asChild = false,
  withCircle = false,
  children,
  ...props
}: React.ComponentProps<'span'> &
  VariantProps<typeof badgeVariants> & {
    asChild?: boolean;
    /** When true, prefixes the label with a filled dot in the badge's accent color. Ignored when `asChild`. */
    withCircle?: boolean;
  }) => {
  // `asChild` renders via Slot, which requires exactly one child — so the dot
  // is only ever rendered in the plain `<span>` path (and is documented as ignored when `asChild`).
  if (asChild) {
    return (
      <SlotPrimitive.Slot data-slot='badge' className={cn(badgeVariants({ variant }), className)} {...props}>
        {children}
      </SlotPrimitive.Slot>
    );
  }
  return (
    <span data-slot='badge' className={cn(badgeVariants({ variant }), className)} {...props}>
      {withCircle && <span data-badge-dot className='size-1.5 shrink-0 rounded-full' />}
      {/* A plain label ends in an ellipsis when the badge is narrower than its text, instead of spilling out. */}
      {typeof children === 'string' ? <span className='truncate'>{children}</span> : children}
    </span>
  );
};

NoddiBadge.displayName = 'NoddiBadge';
